import React, { useState, useEffect, useRef } from 'react';
import { logInteraction } from '../utils/utils';
import { getFeatures, cqlString, isAbortError, userMessage, LAYERS } from '../services/wfsClient';

// Google searches are limited to Rawson and Gran San Juan: with only country: 'AR' they also
// returned Rawson, Chubut. Google treats areas as a preference, so results are filtered too.
const SAN_JUAN_AREA = { south: -32.1, west: -68.8, north: -31.4, east: -68.3 };
const mentionsSanJuan = (text) => /san juan/i.test(String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, ''));
const isInSanJuan = (result) => (result.address_components || []).some(c =>
    c.types.includes('administrative_area_level_1') && mentionsSanJuan(c.long_name));


/* LayerControls: Tabs for Layers, Base, Help */
function LayerControls({ labelsOn, setLabelsOn, layersVisible, setLayersVisible, baseMap, setBaseMap, onSearchSelect, onIdSelect, onAddressSelect, onLocateMe, onNotify }) {
    const [expanded, setExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState('base'); // base, help
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    // Query whose search finished without errors; with no suggestions it means "not found"
    const [resultsFor, setResultsFor] = useState(null);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef(null);

    useEffect(() => {
        if (!query || query.trim().length === 0) { setSuggestions([]); return; }

        const q = query.trim();
        const t = setTimeout(async () => {
            if (abortRef.current) abortRef.current.abort();
            const ac = new AbortController();
            abortRef.current = ac;
            setLoading(true);

            const reqs = [];
            const qUpper = q.toUpperCase();
            const failures = [];
            // Si falla una consulta seguimos con las demás, pero avisamos al usuario
            const collectFailure = (err) => {
                if (!isAbortError(err)) failures.push(err);
                return [];
            };

            // -- PARCEL SEARCH --
            if (!qUpper.startsWith('CO')) {
                let cql = '';
                if (qUpper.startsWith('IM')) {
                    const val = q.substring(2).trim();
                    if (val) cql = `id_contribuyente LIKE ${cqlString(val + '%')}`;
                } else {
                    cql = `Nomenclatura LIKE ${cqlString(q + '%')} OR id_contribuyente LIKE ${cqlString(q + '%')} OR domicilio LIKE ${cqlString(`%${qUpper}%`)}`;
                }

                if (cql) {
                    reqs.push(
                        getFeatures(LAYERS.parcelas, { cql, propertyNames: ['Nomenclatura', 'Nombre', 'domicilio', 'id_contribuyente'], maxFeatures: 5, signal: ac.signal })
                            .then(features => features.map(f => ({
                                type: 'parcel',
                                value: f.properties.Nomenclatura,
                                nomen: f.properties.Nomenclatura || '',
                                nombre: f.properties.Nombre || '',
                                domicilio: f.properties.domicilio || '',
                                id: f.properties.id_contribuyente,
                                label: f.properties.Nomenclatura
                            })))
                            .catch(collectFailure)
                    );
                }
            }

            // -- COMMERCE SEARCH --
            if (!qUpper.startsWith('IM')) {
                let val = '';
                if (qUpper.startsWith('CO')) {
                    val = q.substring(2).trim();
                } else {
                    // Generic search: treat input as potential commerce ID
                    val = q;
                }

                if (val) {
                    // If purely numeric, try exact match to avoid type issues with LIKE on numbers
                    // Otherwise use LIKE for pattern matching
                    const isNum = /^\d+$/.test(val);
                    let cql = '';

                    if (isNum) {
                        cql = `id_contribuyente_comercio = ${cqlString(val)}`;
                    } else {
                        // Removed str() cast as it is not supported by this GeoServer version/config.
                        // We search by address and Name (Nombre).
                        const pattern = cqlString(`%${val.toUpperCase()}%`);
                        cql = `domicilio_comercio LIKE ${pattern} OR Nombre LIKE ${pattern}`;
                    }

                    reqs.push(
                        getFeatures(LAYERS.comercios, { cql, maxFeatures: 5, signal: ac.signal })
                            .then(features => features.map(f => {
                                const p = f.properties || {};
                                const id = p.id_contribuyente_comercio || '';
                                const name = p['Razón Social'] || p.razon_social || p.nombre_comercio || '';
                                const dom = p.domicilio_comercio || p.domicilio || '';
                                return {
                                    type: 'commerce',
                                    value: id,
                                    nomen: id,
                                    nombre: name,
                                    domicilio: dom,
                                    id: id,
                                    label: `CO ${id}`
                                };
                            }))
                            .catch(collectFailure)
                    );
                }
            }

            // IDs and nomenclaturas are only in GeoServer; for them Google returns noise like "Argentina"
            const looksLikeId = /^(IM|CO)?\s*\d+$/i.test(q);

            // -- GOOGLE GEOCODING SEARCH --
            if (!looksLikeId && window.google && window.google.maps && window.google.maps.Geocoder && q.length > 3) {
                const geocoder = new window.google.maps.Geocoder();
                reqs.push(
                    new Promise((resolve) => {
                        geocoder.geocode({
                            address: q,
                            bounds: SAN_JUAN_AREA,
                            componentRestrictions: { country: 'AR', administrativeArea: 'San Juan' },
                        }, (results, status) => {
                            if (status === window.google.maps.GeocoderStatus.OK && results) {
                                // Only San Juan; a whole country or province match is not a useful place to fly to
                                const useful = results.filter(r => isInSanJuan(r) &&
                                    !(r.types || []).some(t => t === 'country' || t === 'administrative_area_level_1'));
                                resolve(useful.map(r => ({
                                    type: 'address',
                                    value: r.formatted_address,
                                    label: r.formatted_address,
                                    lat: r.geometry.location.lat(),
                                    lng: r.geometry.location.lng(),
                                    nombre: 'Dirección física'
                                })));
                            } else {
                                resolve([]);
                            }
                        });
                    })
                );
            }

            // -- GOOGLE PLACES SEARCH (Universal) --
            if (!looksLikeId && window.google && window.google.maps && window.google.maps.places && q.length > 2) {
                const service = new window.google.maps.places.AutocompleteService();
                reqs.push(
                    new Promise((resolve) => {
                        service.getPlacePredictions({
                            input: q,
                            componentRestrictions: { country: 'AR' },
                            locationRestriction: SAN_JUAN_AREA,
                        }, (predictions, status) => {
                            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                                resolve(predictions.filter(p => mentionsSanJuan(p.description)).map(p => ({
                                    type: 'google_place',
                                    place_id: p.place_id,
                                    value: p.description,
                                    label: p.structured_formatting.main_text,
                                    nombre: p.structured_formatting.secondary_text || 'Lugar de Google Maps',
                                    domicilio: p.description
                                })));
                            } else {
                                resolve([]);
                            }
                        });
                    })
                );
            }

            try {
                const results = await Promise.all(reqs);
                if (!ac.signal.aborted) {
                    setSuggestions(results.flat());
                    setResultsFor(failures.length > 0 ? null : q);
                    if (failures.length > 0) {
                        console.error('[búsqueda]', failures);
                        onNotify(userMessage(failures[0]));
                    }
                }
            } catch (err) { if (!isAbortError(err)) console.error(err); }
            finally { if (!ac.signal.aborted) setLoading(false); }

        }, 600);
        return () => clearTimeout(t);
    }, [query, onNotify]);

    const selectSuggestion = (s, viaEnter) => {
        setQuery('');
        setSuggestions([]);
        if (s.type === 'commerce') {
            onIdSelect(s.id);
            logInteraction(s.id, viaEnter ? 'busqueda_enter' : 'busqueda_comercio', window.location.href);
        } else if (s.type === 'address') {
            onAddressSelect(s.lat, s.lng);
            logInteraction(s.label, viaEnter ? 'busqueda_direccion_enter' : 'busqueda_direccion', window.location.href);
        } else if (s.type === 'google_place') {
            // We need to resolve coordinates for Google Places
            const ps = new window.google.maps.places.PlacesService(document.createElement('div'));
            ps.getDetails({ placeId: s.place_id, fields: ['geometry'] }, (place, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place || !place.geometry || !place.geometry.location) {
                    onNotify('No se pudo ubicar ese lugar en el mapa.');
                    return;
                }
                onAddressSelect(place.geometry.location.lat(), place.geometry.location.lng());
                logInteraction(s.label, viaEnter ? 'busqueda_google_place_enter' : 'busqueda_google_place', window.location.href);
            });
        } else {
            onSearchSelect(s.nomen);
            logInteraction(s.nomen, viaEnter ? 'busqueda_enter' : 'busqueda_parcela', window.location.href);
        }
    };

    return (
        <div className={`layer-controls ${expanded ? 'expanded' : ''}`}>
            <div className="controls-card">
                <div className="controls-header">
                    <button className="menu-icon" onClick={() => setExpanded(e => !e)} title="Menú de capas y ayuda">☰</button>
                    <div className="search-wrapper">
                        <input
                            className="search-input"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && suggestions.length > 0) selectSuggestion(suggestions[0], true);
                            }}
                            placeholder="Escriba acá su IDcontribuyente o nomenclatura"
                        />
                        {loading && <div className="search-loading-spinner-small"></div>}
                    </div>
                </div>

                {suggestions.length === 0 && !loading && query.trim() !== '' && resultsFor === query.trim() && (
                    <div className="suggestions-list-container">
                        <div className="suggestions-list">
                            <div className="suggestion-empty" role="status">
                                <strong>No encontramos resultados para “{resultsFor}”.</strong>
                                <span>Revisá el número o buscá por ID (ej. IM464904700000), nomenclatura o dirección.</span>
                            </div>
                        </div>
                    </div>
                )}

                {suggestions.length > 0 && (
                    <div className="suggestions-list-container">
                        <div className="suggestions-list">
                            {suggestions.map((s, i) => (
                                <div key={i} onClick={() => selectSuggestion(s, false)} className="suggestion-item">
                                    <div className="suggestion-main">
                                        <div className="suggestion-label">
                                            {s.type === 'commerce' ? <span style={{ color: '#d9534f' }}>🛍️ {s.label}</span> :
                                                s.type === 'address' || s.type === 'google_place' ? <span style={{ color: '#0275d8' }}>📍 {s.label}</span> : s.label}
                                        </div>
                                        <div className="suggestion-name">{s.nombre}</div>
                                    </div>
                                    <div className="suggestion-details">
                                        <div className="suggestion-id">{s.type === 'parcel' ? `IM${s.id}` : ''}</div>
                                        <div className="suggestion-address">{s.domicilio}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {expanded && (
                    <div className="controls-content" style={{ padding: 0 }}>

                        <div className="controls-tabs">
                            <button
                                className={`tab-btn ${activeTab === 'base' ? 'active' : ''}`}
                                onClick={() => setActiveTab('base')}
                            >
                                <span className="tab-icon">🌍</span>
                                Base
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'help' ? 'active' : ''}`}
                                onClick={() => setActiveTab('help')}
                            >
                                <span className="tab-icon">❓</span>
                                Ayuda
                            </button>
                        </div>

                        <div className="tab-content" style={{ padding: '12px', minHeight: '200px' }}>

                            {activeTab === 'base' && (
                                <div className="layer-list fadeIn">
                                    <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Mapa Base</div>
                                    <label className="layer-item">
                                        <input type="radio" name="basemap" checked={baseMap === 'osm'} onChange={() => setBaseMap('osm')} /> OpenStreetMap
                                    </label>
                                    <label className="layer-item">
                                        <input type="radio" name="basemap" checked={baseMap === 'topo'} onChange={() => setBaseMap('topo')} /> Topografía
                                    </label>
                                    <label className="layer-item">
                                        <input type="radio" name="basemap" checked={baseMap === 'satellite'} onChange={() => setBaseMap('satellite')} /> Esri Satélite
                                    </label>
                                    <label className="layer-item">
                                        <input type="radio" name="basemap" checked={baseMap === 'google-road'} onChange={() => setBaseMap('google-road')} /> Google Calles
                                    </label>
                                    <label className="layer-item">
                                        <input type="radio" name="basemap" checked={baseMap === 'google-satellite'} onChange={() => setBaseMap('google-satellite')} /> Google Satélite
                                    </label>
                                    <label className="layer-item">
                                        <input type="radio" name="basemap" checked={baseMap === 'google-hybrid'} onChange={() => setBaseMap('google-hybrid')} /> Google Híbrido
                                    </label>
                                </div>
                            )}


                            {activeTab === 'help' && (
                                <div className="fadeIn" style={{ fontSize: '14px', color: '#444', lineHeight: '1.6' }}>
                                    <div style={{ marginBottom: 10, fontWeight: 'bold', fontSize: '16px', color: '#007bff' }}>Bienvenido al GeoPortal Rawson</div>

                                    <div style={{ marginBottom: 12 }}>
                                        <strong style={{ color: '#007bff' }}>🔍 Búsqueda</strong>
                                        <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
                                            <li style={{ marginBottom: 4 }}>Ingrese su <strong>ID de Contribuyente</strong> o <strong>Nomenclatura</strong> en el buscador</li>
                                            <li style={{ marginBottom: 4 }}>Para comercios, use el prefijo <strong>CO</strong> seguido del ID (ej: CO5722)</li>
                                            <li style={{ marginBottom: 4 }}>Para inmuebles, use el prefijo <strong>IM</strong> o directamente el ID</li>
                                        </ul>
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <strong style={{ color: '#007bff' }}>🗺️ Navegación</strong>
                                        <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
                                            <li style={{ marginBottom: 4 }}>Haga <strong>clic en el mapa</strong> para ver información de parcelas y comercios</li>
                                            <li style={{ marginBottom: 4 }}>Use la rueda del mouse para hacer <strong>zoom</strong></li>
                                            <li style={{ marginBottom: 4 }}>Arrastre el mapa para desplazarse</li>
                                        </ul>
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <strong style={{ color: '#007bff' }}>📋 Panel de Información</strong>
                                        <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
                                            <li style={{ marginBottom: 4 }}>Vea datos de <strong>Inmueble</strong> y <strong>Comercio</strong> en pestañas separadas</li>
                                            <li style={{ marginBottom: 4 }}>Use los botones <strong>Pagar Ahora</strong> para gestionar pagos</li>
                                            <li style={{ marginBottom: 4 }}>Botón <strong>Más Info</strong> para contactar por WhatsApp</li>
                                        </ul>
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <strong style={{ color: '#007bff' }}>🌍 Mapas Base</strong>
                                        <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
                                            <li style={{ marginBottom: 4 }}>Cambie entre diferentes fondos de mapa en la pestaña <strong>Base</strong></li>
                                            <li style={{ marginBottom: 4 }}>Opciones: OpenStreetMap, Satélite, Híbrido, etc.</li>
                                        </ul>
                                    </div>

                                    {/* Standalone geolocation button removed from here */}

                                    <div style={{ marginTop: 12, padding: 8, background: '#eee', borderRadius: 4, fontSize: 11, textAlign: 'center' }}>
                                        <strong>GeoPortal Rawson v1.3.0</strong><br />
                                        Municipalidad de Rawson<br />
                                        <span style={{ fontSize: 10, color: '#666' }}>© 2025 - Todos los derechos reservados</span>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LayerControls;
